// Native <dialog> styles used by HubModal. Ported from the Hono worker's
// ui/modal.tsx (PAGE_STYLES + MODAL_STYLES) — kept inline so the SPA has no
// CSS-import build dependency. Once @mf/ui ships a Modal component this file
// goes away.
export const HUB_MODAL_CSS = `
:root {
  /* Neutral shadcn-style base (light / MoGu). applyBrandTheme() overrides
     these per resolved brand (MoGu light, OrX dark) before first paint. */
  --hub-bg: #ffffff;
  --hub-surface: #f5f5f5;
  --hub-card: #ffffff;
  --hub-ink: #0a0a0a;
  --hub-ink-soft: #737373;
  --hub-muted: #737373;
  --hub-line: #e5e5e5;
  --hub-accent: #171717;
  --hub-accent-600: #000000;
  --hub-on-accent: #fafafa;
  --hub-ring: #a3a3a3;
  --hub-danger: #dc2626;
  --hub-radius: 8px;
  --hub-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.06);
  --hub-font:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, "Helvetica Neue",
    Arial, sans-serif;
}
body {
  margin: 0;
  background: var(--hub-bg);
  color: var(--hub-ink);
  font-family: var(--hub-font);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
.hub-page-header { max-width: 760px; margin: 0 auto; padding: 48px 24px 16px; }
.hub-page-header h1 { margin: 0 0 8px; font-size: 28px; font-weight: 700; letter-spacing: -0.01em; }
.hub-page-header p { margin: 0; color: var(--hub-ink-soft); font-size: 15px; }
.hub-explainer {
  max-width: 760px;
  margin: 24px auto 0;
  padding: 20px 24px;
  background: var(--hub-card);
  border: 1px solid var(--hub-line);
  border-radius: var(--hub-radius);
}
.hub-explainer h2 {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: var(--hub-ink-soft);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.hub-qa-list { margin: 0; padding: 0 0 0 20px; color: var(--hub-ink); font-size: 14px; }
.hub-qa-list li { margin: 6px 0; }
.hub-cta-row { max-width: 760px; margin: 28px auto 64px; padding: 0 24px; text-align: center; }
.hub-cta-button {
  display: inline-block;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 600;
  font-family: var(--hub-font);
  color: var(--hub-on-accent);
  background: var(--hub-accent);
  border: 1px solid var(--hub-accent);
  border-radius: var(--hub-radius);
  cursor: pointer;
  transition: background-color 0.15s, transform 0.05s;
  text-decoration: none;
}
.hub-cta-button:hover { background: var(--hub-accent-600); border-color: var(--hub-accent-600); }
.hub-cta-button:active { transform: translateY(1px); }
.hub-cta-button.secondary { background: var(--hub-card); color: var(--hub-ink); border-color: var(--hub-line); }
.hub-cta-button.secondary:hover { background: var(--hub-surface); }
.hub-cta-button + .hub-cta-button { margin-left: 12px; }
.hub-error-banner {
  max-width: 760px;
  margin: 24px auto 0;
  padding: 14px 18px;
  background: var(--hub-card);
  color: var(--hub-danger);
  border: 1px solid var(--hub-line);
  border-left: 3px solid var(--hub-danger);
  border-radius: var(--hub-radius);
  font-size: 14px;
}
.hub-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 24px 0;
}
.hub-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--hub-brand-accent, var(--hub-accent));
  color: var(--hub-on-accent);
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
  flex: none;
}
.hub-identity-meta { display: flex; flex-direction: column; line-height: 1.3; }
.hub-identity-label { font-size: 12px; color: var(--hub-muted); text-transform: uppercase; letter-spacing: 0.06em; }
.hub-identity-value { font-size: 15px; font-weight: 600; color: var(--hub-ink); }
.hub-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  vertical-align: -3px;
  border: 2px solid color-mix(in srgb, var(--hub-on-accent) 40%, transparent);
  border-top-color: var(--hub-on-accent);
  border-radius: 50%;
  animation: hub-spin 0.7s linear infinite;
}
@keyframes hub-spin { to { transform: rotate(360deg); } }
.hub-depot-ready {
  max-width: 760px;
  margin: 24px auto 0;
  padding: 16px 20px;
  background: var(--hub-card);
  color: var(--hub-ink);
  border: 1px solid var(--hub-line);
  border-left: 3px solid var(--hub-accent);
  border-radius: var(--hub-radius);
  font-size: 15px;
}
.hub-depot-ready code { font-weight: 700; }
.hub-check { color: var(--hub-ink); font-weight: 700; margin-right: 6px; }
.hub-modal {
  width: min(720px, calc(100vw - 32px));
  height: min(820px, calc(100vh - 48px));
  padding: 0;
  border: 1px solid var(--hub-line);
  border-radius: 10px;
  box-shadow: var(--hub-shadow);
  background: var(--hub-card);
  overflow: hidden;
}
.hub-modal::backdrop { background: rgba(0, 0, 0, 0.55); backdrop-filter: blur(2px); }
.hub-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--hub-line);
  background: var(--hub-surface);
  font-size: 12.5px;
  color: var(--hub-muted);
}
.hub-modal-head code { color: var(--hub-ink-soft); font-weight: 600; }
.hub-modal-close {
  appearance: none;
  background: transparent;
  border: 0;
  color: var(--hub-ink-soft);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 4px;
}
.hub-modal-close:hover { background: var(--hub-line); }
.hub-modal-frame {
  width: 100%;
  height: calc(100% - 41px);
  border: 0;
  display: block;
  background: var(--hub-surface);
}
`;

export function ensureModalStylesheet(): void {
  // Idempotent — module may be imported by multiple components/tests.
  if (typeof document === "undefined") return;
  const id = "hub-modal-stylesheet";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = HUB_MODAL_CSS;
  document.head.appendChild(style);
}
